/**
 * Flythrough playback driver.
 *
 * Owns a private rAF clock and writes the camera with `map.jumpTo()` every
 * frame. That is the whole reason this doesn't chain `easeTo`: `easeTo` runs a
 * self-contained animation per segment and settles at rest, so a chain of them
 * necessarily stops at every waypoint. Driving the clock ourselves lets a single
 * continuous spline span the entire tour.
 *
 * Rendering: `jumpTo` triggers a Mapbox repaint, which drives the Three.js
 * custom layer's `render()`. `markSceneDirty()` is NOT called and must not be —
 * camera-only motion mutates no scene object, and calling it would add a
 * redundant `triggerRepaint()` per frame.
 *
 * React: the store's `elapsedMs` is written ~10x/sec for the scrubber, not every
 * frame, so the component tree doesn't re-render at 60Hz.
 */
import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import type { MapboxCustomLayerService } from '@/services/3d'
import { useCinemaStore } from '@/stores/cinemaStore'
import { compileTour, isPoseFinite, sampleTour } from './tour'
import { selectActivePlaylist, useFlythroughStore } from './flythroughStore'
import type { CameraPose, CompiledTour } from './types'

/** How often the store's elapsed clock is written (ms). */
const STORE_WRITE_MS = 100

/** Guard against a huge dt after a background-tab stall. */
const MAX_FRAME_MS = 100

/** One-shot ease into the opening shot before the spline takes over. */
const LEAD_IN_MS = 600

/** Ease back to the pre-playback camera on stop. */
const RESTORE_MS = 600

/** Mapbox gesture handlers we suspend during playback. */
const HANDLERS = [
  'boxZoom',
  'scrollZoom',
  'dragPan',
  'dragRotate',
  'keyboard',
  'touchZoomRotate',
  'doubleClickZoom',
] as const

type HandlerName = (typeof HANDLERS)[number]

interface Options {
  map: mapboxgl.Map | null
  layerService: MapboxCustomLayerService | null
  /** Opaque key identifying which surface's playlists to fly. */
  scope: string
}

export function useFlythroughPlayback({ map, layerService, scope }: Options) {
  const isPlaying = useFlythroughStore((s) => s.isPlaying)
  const playingScope = useFlythroughStore((s) => s.playingScope)

  const rafRef = useRef<number | null>(null)
  const tourRef = useRef<CompiledTour | null>(null)
  const clockRef = useRef(0)
  const lastFrameRef = useRef(0)
  const lastWriteRef = useRef(0)
  const savedCameraRef = useRef<CameraPose | null>(null)
  const savedHandlersRef = useRef<Partial<Record<HandlerName, boolean>>>({})
  const hidUiRef = useRef(false)
  const pausedForHiddenRef = useRef(false)

  const active = isPlaying && playingScope === scope

  /** Suspend map gestures, remembering which were on so we restore faithfully. */
  const lockInteraction = useCallback((m: mapboxgl.Map) => {
    const saved: Partial<Record<HandlerName, boolean>> = {}
    for (const name of HANDLERS) {
      const handler = m[name] as { isEnabled?: () => boolean; disable?: () => void } | undefined
      if (!handler?.isEnabled) continue
      saved[name] = handler.isEnabled()
      handler.disable?.()
    }
    savedHandlersRef.current = saved
  }, [])

  /**
   * Restore only the handlers that were enabled before. A blanket `enable()`
   * would switch on gestures the page had legitimately turned off (e.g. the
   * ramp's drag-and-drop toggle).
   */
  const unlockInteraction = useCallback((m: mapboxgl.Map) => {
    for (const [name, wasEnabled] of Object.entries(savedHandlersRef.current)) {
      if (!wasEnabled) continue
      const handler = m[name as HandlerName] as { enable?: () => void } | undefined
      handler?.enable?.()
    }
    savedHandlersRef.current = {}
  }, [])

  // ── The frame loop ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!active || !map) return

    const playlist = selectActivePlaylist(useFlythroughStore.getState(), scope)
    if (!playlist) {
      useFlythroughStore.getState().endPlayback()
      return
    }

    const tour = compileTour(playlist)
    if (!tour) {
      toast.error('This tour needs at least two shots with a duration.')
      useFlythroughStore.getState().endPlayback()
      return
    }
    tourRef.current = tour

    savedCameraRef.current = {
      center: [map.getCenter().lng, map.getCenter().lat],
      zoom: map.getZoom(),
      pitch: map.getPitch(),
      bearing: map.getBearing(),
    }

    lockInteraction(map)
    // Pin aircraft detail: a shot that dips below the LOD zoom threshold would
    // otherwise swap every model for a grey box mid-move.
    layerService?.setLODEnabled(false)

    if (playlist.hideUi) {
      hidUiRef.current = true
      useCinemaStore.getState().enter('playback')
    }

    let cancelled = false

    // Sanitized once: a NaN here would poison the clock on the first frame.
    const speed =
      typeof playlist.speed === 'number' && Number.isFinite(playlist.speed) && playlist.speed > 0
        ? playlist.speed
        : 1

    const step = (now: number) => {
      if (cancelled) return
      const dt = Math.min(now - lastFrameRef.current, MAX_FRAME_MS)
      lastFrameRef.current = now

      clockRef.current += dt * speed

      let t = clockRef.current
      // A non-finite clock would make `t >= totalMs` false forever.
      if (!Number.isFinite(t)) {
        console.error('[flythrough] clock went non-finite, aborting playback')
        useFlythroughStore.getState().endPlayback()
        return
      }
      if (t >= tour.totalMs) {
        if (playlist.loop) {
          t = tour.totalMs > 0 ? t % tour.totalMs : 0
          clockRef.current = t
        } else {
          const finalPose = sampleTour(tour, tour.totalMs)
          if (isPoseFinite(finalPose)) map.jumpTo(finalPose)
          useFlythroughStore.getState().endPlayback()
          return
        }
      }

      // Anything thrown in here would kill the rAF callback WITHOUT clearing
      // `isPlaying` — playback would freeze with the button stuck on "Stop" and
      // no way to end it short of a reload. Always fail closed instead.
      let pose
      try {
        pose = sampleTour(tour, t)
      } catch (err) {
        console.error('[flythrough] sampling threw, aborting playback', err)
        toast.error('Flythrough aborted — the tour could not be evaluated.')
        useFlythroughStore.getState().endPlayback()
        return
      }

      if (!isPoseFinite(pose)) {
        // A NaN reaching Mapbox throws inside the render loop and kills the map.
        console.error('[flythrough] non-finite camera pose, aborting playback', pose)
        toast.error('Flythrough aborted — the tour produced an invalid camera position.')
        useFlythroughStore.getState().endPlayback()
        return
      }

      try {
        map.jumpTo(pose)
      } catch (err) {
        console.error('[flythrough] jumpTo threw, aborting playback', err)
        useFlythroughStore.getState().endPlayback()
        return
      }

      if (now - lastWriteRef.current >= STORE_WRITE_MS) {
        lastWriteRef.current = now
        useFlythroughStore.getState().setElapsed(t)
      }

      rafRef.current = requestAnimationFrame(step)
    }

    // One-shot lead-in to the opening shot, so playback never teleports and a
    // loop (which carries no ramp) still starts from rest.
    const startLoop = () => {
      if (cancelled) return
      clockRef.current = 0
      lastFrameRef.current = performance.now()
      lastWriteRef.current = lastFrameRef.current
      rafRef.current = requestAnimationFrame(step)
    }

    let fallbackTimer: number | null = null
    let cleanupLeadIn: (() => void) | null = null

    const opening = sampleTour(tour, 0)
    if (isPoseFinite(opening)) {
      const beginOnce = () => {
        if (fallbackTimer != null) {
          window.clearTimeout(fallbackTimer)
          fallbackTimer = null
        }
        startLoop()
      }
      map.once('moveend', beginOnce)
      map.easeTo({ ...opening, duration: LEAD_IN_MS })
      // Safety net: if `moveend` never fires (camera already at the pose, or the
      // ease gets interrupted), start anyway so playback can't hang.
      fallbackTimer = window.setTimeout(() => {
        fallbackTimer = null
        map.off('moveend', beginOnce)
        startLoop()
      }, LEAD_IN_MS + 250)
      cleanupLeadIn = () => {
        if (fallbackTimer != null) window.clearTimeout(fallbackTimer)
        map.off('moveend', beginOnce)
      }
    } else {
      startLoop()
    }

    return () => {
      cancelled = true
      cleanupLeadIn?.()
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      map.stop()
      unlockInteraction(map)
      layerService?.setLODEnabled(true)

      if (hidUiRef.current) {
        hidUiRef.current = false
        // Only reverse OUR change — a user who hit the cinema toggle themselves
        // mid-tour should stay hidden.
        if (useCinemaStore.getState().source === 'playback') useCinemaStore.getState().exit()
      }

      const saved = savedCameraRef.current
      if (saved && playlist.restoreCameraOnStop) {
        map.easeTo({ ...saved, duration: RESTORE_MS })
      }
      savedCameraRef.current = null
      tourRef.current = null
    }
  }, [active, map, layerService, scope, lockInteraction, unlockInteraction])

  // ── Background-tab handling ───────────────────────────────────────────────
  // rAF throttles to ~1Hz (or stops) when the tab is hidden. Without resetting
  // the frame timestamp on return, the first frame back would carry a multi-
  // second dt and jump the tour forward.
  useEffect(() => {
    if (!active) return
    const onVisibility = () => {
      if (document.hidden) {
        pausedForHiddenRef.current = true
      } else if (pausedForHiddenRef.current) {
        pausedForHiddenRef.current = false
        lastFrameRef.current = performance.now()
        lastWriteRef.current = lastFrameRef.current
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [active])
}
